import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Memoized logout to prevent unnecessary re-renders
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setAuthError(null);
  }, []);

  // Fetch user with defensive error handling
  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    // Skip /auth/me on public auth pages — they don't need the user
    // and a stale token here can stall the UI behind a hung request.
    const publicAuthPaths = ['/verify-email', '/reset-password', '/forgot-password'];
    if (typeof window !== 'undefined' && publicAuthPaths.includes(window.location.pathname)) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000, // fail fast on hung requests instead of waiting browser default (~120s)
      });
      setUser(response.data);
      setAuthError(null);
    } catch (error) {
      console.error('Auth fetch error:', error);
      
      // DEFENSIVE: Only logout on explicit token invalidity
      // NOT on network errors, server errors, or transient issues
      const status = error.response?.status;
      const detail = error.response?.data?.detail || '';
      
      const isTokenInvalid = 
        status === 401 && (
          detail === 'Token expired' ||
          detail === 'Invalid token' ||
          detail === 'Not authenticated' ||
          detail === 'Could not validate credentials'
        );
      
      if (isTokenInvalid) {
        console.warn('Token invalid/expired - logging out');
        logout();
      } else {
        // Keep user logged in for transient errors (network, 500, etc.)
        // Just set error state so UI can show retry option
        console.warn('Transient auth error - keeping session:', status, detail);
        setAuthError(error.message || 'Connection error');
        // Don't clear user if we had one before
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setAuthError(null);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setAuthError(null);
    return userData;
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Retry fetching user (for UI retry button)
  const retryAuth = () => {
    setAuthError(null);
    fetchUser();
  };

  const value = {
    user,
    token,
    loading,
    authError,
    login,
    register,
    logout,
    updateUser,
    retryAuth,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
